import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { Circle, Polygon } from '@react-google-maps/api';
import { postGISToGoogleMapsPoints } from '@trucktrax/trucktrax-common';
import {
  GeoZoneDto,
  LocationDto,
  OrderDto,
  PlantDto,
  ProductLine,
  TicketDto,
  UrlKeyDto,
  UserPreferencesDto,
  UserRegionDto,
} from '@trucktrax/trucktrax-ts-common';
import isEqual from 'lodash/isEqual';
import React, { Component } from 'react';
import { createRoot, Root } from 'react-dom/client';
import config from 'react-global-configuration';
import { connect } from 'react-redux';

import { GEOZONE_SHAPE_STYLE, GEOZONE_TYPE } from '../../../constants/appConstants';
import * as constants from '../../../constants/mapConstants';
import {
  DirectionsCacheService,
  directionsCacheService,
} from '../../../services/directionsCacheService';
import { getDirections } from '../../../services/mapviewsService';
import { fetchPlant } from '../../../services/plantsService';
import { getUserPreferences } from '../../../services/userPreferencesService';
import { ReduxState } from '../../../store';
import { cacheDirectionsResult } from '../../../store/actions/directionsCacheActions';
import {
  MarkerSelectedFrom,
  MarkerSelectedFromSource,
} from '../../../store/actions/mapviewActions';
import { isMarkerOpen, PopupToggleMap } from '../../../store/reducers/mapReducers';
import {
  OpenMapMarker,
  sortedOpenMarkersMatchingMapFilter,
} from '../../../store/selectors/mapViewSelectors';
import {
  MAP_TYPES,
  MapMarker,
  MapMarkerType,
  MapTypes,
  PositionDetailsDto,
  ZoneType,
} from '../../../types';
import { determineRegionId, getIdFromUrl } from '../../../util/appUtil';
import recordLog, { LogLevel } from '../../../util/errorUtil';
import {
  determineMapCurrentCenter,
  determineMapType,
  isValidLatLng,
  mapsToLocationDto,
} from '../../../util/geoUtil';
import { determineMapMarkerId, PositionMarkerDataInfo } from '../../../util/markerUtil';
import mapStyles from './map-style';
import { DefaultLocation, MapChangeEvent } from './mapTypes';
import JobsiteMarker from './markers/JobsiteMarker';
import markerColors from './markers/MarkerColors';
import {
  Z_INDEX_INFOPANE_MIDDLE,
  Z_INDEX_INFOPANE_TOP,
  Z_INDEX_MARKER_BOTTOM,
  Z_INDEX_MARKER_MIDDLE,
  Z_INDEX_MARKER_TOP,
} from './markers/markerConstants';
import PlantMarkerWithInfoPane from './markers/PlantMarkerWithInfoPane';
import PositionMarker from './markers/PositionMarker';
import RecenterMapBtn from './RecenterMapBtn';
import TrafficBtn from './TrafficBtn';

export type ShapeStyles = {
  fillColor: string;
  strokeColor: string;
  fillOpacity: number;
  strokeWeight: number;
  clickable: boolean;
  zIndex: number;
  draggable?: boolean;
  editable?: boolean;
};

const containerStyle = {
  width: '100%',
  height: '100%',
};

export class TTMap extends Component<TTMapProps, TTMapState> {
  circleRef?: google.maps.Circle;

  listenersRef?: google.maps.MapsEventListener[];

  map?: google.maps.Map;

  mapRef: React.RefObject<HTMLDivElement>;

  polygonRef?: google.maps.Polygon;

  directionsRenderer?: google.maps.DirectionsRenderer | null;

  currentRouteMarkerId?: string | null;

  gmapControlPositions = window.google.maps.ControlPosition;

  mapPositions = {
    leftBottom: this.gmapControlPositions.LEFT_BOTTOM,
    rightBottom: this.gmapControlPositions.RIGHT_BOTTOM,
    topRight: this.gmapControlPositions.TOP_RIGHT,
  };

  defaultMapType = MAP_TYPES.ROADMAP;

  selectedMapTypeRef = React.createRef<string>();

  traffic: google.maps.TrafficLayer | null = null;

  // track created React roots for cleanup of map controls

  private controlRoots: Root[] = [];

  // cancellable timeout id for deferred control root cleanup
  private controlRootsCleanupTimeout?: number;

  // pending traffic preference from user settings, applied after map is ready
  private pendingTrafficPreference: boolean = false;

  constructor(props: TTMapProps) {
    super(props);
    this.state = {
      showTraffic: false,
      isMapHasBeenBounded: false,
      selectedMapType: this.defaultMapType,
      pauseMapRendering: false,
      currentZoom: undefined,
      shouldUpdateCenter: null,
      shouldUpdateZoom: null,
      followedMarkerId: null,
      routeErrorDialogOpen: false,
      routeErrorMessage: '',
    };

    this.mapRef = React.createRef<HTMLDivElement>();
  }

  /** brings last selected to top and the rest of selected above unselected */
  calculateZIndex = (truckId: string, isSelected: boolean, isInfoPane: boolean = false) => {
    const { lastMarkerSelected } = this.props.popupToggleMap;
    // InfoPanes need higher z-index to appear above details markers
    if (isInfoPane) {
      return lastMarkerSelected === truckId && isSelected
        ? Z_INDEX_INFOPANE_TOP
        : Z_INDEX_INFOPANE_MIDDLE;
    }
    if (lastMarkerSelected === truckId && isSelected) {
      return Z_INDEX_MARKER_TOP;
    }
    return isSelected ? Z_INDEX_MARKER_MIDDLE : Z_INDEX_MARKER_BOTTOM;
  };

  shouldComponentUpdate(
    _nextProps: Readonly<TTMapProps>,
    nextState: Readonly<TTMapState>,
  ): boolean {
    if (!this.mapRef.current) {
      return false;
    }

    const shouldPauseRenderingForMapDragging =
      this.state.pauseMapRendering || nextState.pauseMapRendering;
    return !shouldPauseRenderingForMapDragging;
  }

  async componentDidMount() {
    this.initializeMap();
    const userPreferences = await this.fetchUserPreferences();
    const { currentRegion, selectedProductLine } = this.props;
    const regionId = getIdFromUrl(currentRegion.url);

    const preferencesForRegionAndProductline =
      userPreferences?.mapFilters?.[regionId]?.[selectedProductLine!];

    const { viewLiveTraffic } = preferencesForRegionAndProductline || { viewLiveTraffic: false };

    // Store preference to apply after map is ready (in tilesloaded event)
    this.pendingTrafficPreference = viewLiveTraffic;
  }

  componentDidUpdate(prevProps: Readonly<TTMapProps>): void {
    if (!this.map) {
      return;
    }

    const centerChanged = this.props.center && !isEqual(prevProps.center, this.props.center);
    const isValidCenterChange =
      centerChanged && isValidLatLng(this.props.center.lat, this.props.center.lng);
    const zoomChanged = prevProps.currentZoom !== this.props.currentZoom;
    const shouldUpdateMapView = isValidCenterChange || zoomChanged;
    if (shouldUpdateMapView) {
      this.map.setOptions({ center: this.props.center, zoom: this.props.currentZoom });
    }

    this.ensureFollowedMarkerStillOpen();
    this.ensurePredictiveRouteMarkerStillActive();
    this.handleFollowUpdate(prevProps);
  }

  private ensureFollowedMarkerStillOpen(): void {
    const { followedMarkerId } = this.state;
    if (!followedMarkerId) {
      return;
    }

    const { sortedOpenMarkers } = this.props;
    const activeMarkers = this.props.activeMarkers as MapMarker[];

    const isStillOpen = (sortedOpenMarkers as OpenMapMarker[]).some(
      (marker: OpenMapMarker) => determineMapMarkerId(marker) === followedMarkerId,
    );
    const isStillActive = activeMarkers.some(
      (marker: MapMarker) => determineMapMarkerId(marker) === followedMarkerId,
    );

    if (!isStillOpen || !isStillActive) {
      this.setState({ followedMarkerId: null });
    }
  }

  private ensurePredictiveRouteMarkerStillActive(): void {
    if (!this.currentRouteMarkerId) {
      return;
    }

    const activeMarkers = this.props.activeMarkers as MapMarker[];
    const isStillActive = activeMarkers.some(
      (marker: MapMarker) => determineMapMarkerId(marker) === this.currentRouteMarkerId,
    );

    if (!isStillActive) {
      this.clearPredictiveRoute(undefined, 'marker-removed');
    }
  }

  private handleFollowToggle = (markerId: string, center?: { lat: number; lng: number }): void => {
    const { followedMarkerId } = this.state;
    const nextFollowedMarkerId = followedMarkerId === markerId ? null : markerId;
    this.setState({ followedMarkerId: nextFollowedMarkerId }, () => {
      if (!this.map || !nextFollowedMarkerId || !center) {
        return;
      }

      if (!isValidLatLng(center.lat, center.lng)) {
        return;
      }

      this.map.panTo(center);
      this.props.handleCenterChanged?.(center);
    });
  };

  private handleFollowUpdate(prevProps: Readonly<TTMapProps>): void {
    const { followedMarkerId, pauseMapRendering } = this.state;
    if (!followedMarkerId || !this.map || pauseMapRendering) {
      return;
    }

    const previousCenter = TTMap.findMarkerCenter(
      prevProps.activeMarkers as MapMarker[],
      followedMarkerId,
    );
    const nextCenter = TTMap.findMarkerCenter(
      this.props.activeMarkers as MapMarker[],
      followedMarkerId,
    );

    if (!nextCenter) {
      return;
    }

    const centerChanged =
      !previousCenter ||
      previousCenter.lat !== nextCenter.lat ||
      previousCenter.lng !== nextCenter.lng;

    if (!centerChanged) {
      return;
    }

    this.map.panTo(nextCenter);
    this.props.handleCenterChanged?.(nextCenter);
  }

  private static findMarkerCenter(
    markers: MapMarker[],
    markerId: string,
  ): { lat: number; lng: number } | null {
    const marker = markers.find((m: MapMarker) => determineMapMarkerId(m) === markerId);
    const location = marker?.data?.positionDetails?.location;
    const lat = location?.lat ?? location?.latitude;
    const lng = location?.lng ?? location?.longitude;
    if (!isValidLatLng(lat, lng)) {
      return null;
    }
    return { lat: lat!, lng: lng! };
  }

  initializeMap(): google.maps.Map | undefined {
    if (window.google && window.google.maps) {
      // Try build-time env var first, then runtime config, then fallback
      const mapId =
        process.env.REACT_APP_GOOGLE_MAP_ID || config.get('REACT_APP_GOOGLE_MAP_ID') || undefined;

      recordLog(LogLevel.Debug, '[TTMap] Initializing map with Map ID:', {
        mapId,
        fromEnv: process.env.REACT_APP_GOOGLE_MAP_ID,
        fromConfig: config.get('REACT_APP_GOOGLE_MAP_ID'),
      });

      this.map = new window.google.maps.Map(this.mapRef.current as HTMLDivElement, {
        center: this.props.center,
        zoom: this.props.currentZoom,
        streetViewControl: true,
        streetViewControlOptions: {
          position: google.maps.ControlPosition.RIGHT_BOTTOM,
        },
        mapTypeControl: true,
        fullscreenControl: false,
        rotateControl: false,
        controlSize: constants.GM_CONTROLSIZE,
        // Only include a mapId if it is explicitly provided; otherwise leave
        // undefined so styles can be applied client-side when desired.
        mapId,
        clickableIcons: false,
        mapTypeControlOptions: {
          mapTypeIds: [MAP_TYPES.ROADMAP, MAP_TYPES.SATELLITE, MAP_TYPES.HYBRID],
          // style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR, // not compatible with mapId
          position: this.mapPositions.topRight,
        },
        // Don't set styles when a real mapId is present (cloud-managed styles).
        styles: process.env.REACT_APP_GOOGLE_MAP_ID ? undefined : mapStyles,
        disableDoubleClickZoom: false,
      });

      this.registerMapEvents(this.map);

      return this.map;
    }

    recordLog(LogLevel.Warn, 'Google Maps API not loaded yet!');
    return undefined;
  }

  registerMapEvents(map: google.maps.Map): void {
    if (!map) {
      return;
    }

    // addListenerOnce ensures this fires only once per map instance (page load)
    google.maps.event.addListenerOnce(map, 'tilesloaded', () => {
      // Notify parent that map is fully ready for markers (this ensures AdvancedMarkerElement works properly)
      if (this.props.onMapReady) {
        this.props.onMapReady();
      }

      // Render buttons in correct order: re-center above traffic (matching production)
      this.renderRecenterMapButton();
      this.showLiveTraffic(this.pendingTrafficPreference, true);

      map.addListener('zoom_changed', () => {
        const currentZoom = this.map!.getZoom();
        if (currentZoom === this.state.currentZoom) {
          return;
        }

        this.setState({ shouldUpdateZoom: true, pauseMapRendering: true });
      });

      map.addListener('maptypeid_changed', () => {
        this.traffic = this.generateNewTrafficLayer();
        if (this.state.showTraffic && this.traffic && map) {
          this.traffic.setMap(map);
        }
        this.handleMapTypeStyleUpdates();
        const currentMapType = determineMapType(map) as string;
        if (this.selectedMapTypeRef.current !== currentMapType) {
          this.setState({ selectedMapType: currentMapType });
        }
      });

      map.addListener('idle', () => {
        if (!this.state.pauseMapRendering) {
          return;
        }

        this.setState({ pauseMapRendering: false });
        const location = determineMapCurrentCenter(map);
        const updateCenter =
          this.state.shouldUpdateCenter && location && this.props.handleCenterChanged;
        if (updateCenter) {
          this.props.handleCenterChanged!(location);
          this.setState({ shouldUpdateCenter: null });
        }

        const currentZoom = this.map?.getZoom();
        const updateZoom = this.state.shouldUpdateZoom && currentZoom !== undefined;
        if (updateZoom) {
          this.setState({ shouldUpdateZoom: null, currentZoom });
          this.handleZoomChanged(currentZoom);
        }
      });

      map.addListener('drag', () => {
        if (!this.state.pauseMapRendering) {
          this.setState({ pauseMapRendering: true, shouldUpdateCenter: true });
        }
      });
    });
  }

  componentWillUnmount() {
    this.handleMapUnmountCleanUp();

    if (this.controlRootsCleanupTimeout) {
      window.clearTimeout(this.controlRootsCleanupTimeout);
      this.controlRootsCleanupTimeout = undefined;
    }

    if (this.controlRoots && this.controlRoots.length > 0) {
      this.controlRootsCleanupTimeout = window.setTimeout(() => {
        this.controlRoots.forEach(root => {
          try {
            root.unmount();
          } catch (e) {
            // ignore
          }
        });
        this.controlRoots = [];
        this.controlRootsCleanupTimeout = undefined;
      }, 0);
    }
  }

  handleMapUnmountCleanUp = () => {
    if (this.traffic) {
      try {
        this.traffic.setMap(null);
      } catch (e) {
        recordLog(LogLevel.Debug, 'Error removing map traffic layer on unmount', { error: e });
      }
      this.traffic = null;
    }

    try {
      const googleMapRef = window.google?.maps;
      if (googleMapRef && this.map) {
        googleMapRef.event.clearInstanceListeners(this.map);
      }
    } catch (e) {
      recordLog(LogLevel.Debug, 'Error removing map listeners on unmount', { error: e });
    }
  };

  fetchUserPreferences = async () => {
    const userPreferences = await getUserPreferences(this.props.userUrl!);
    return userPreferences;
  };

  showLiveTraffic = (show: boolean, initialRender: boolean = false) => {
    if (!this.map) {
      return;
    }

    this.traffic = this.generateNewTrafficLayer();

    if (show) {
      const isLiveTrafficViewMapType = this.canCurrentMapTypeShowLiveTraffic();
      if (isLiveTrafficViewMapType) {
        this.traffic.setMap(this.map);
      }
    }

    if (!initialRender) {
      this.map.controls[this.mapPositions.rightBottom].pop();
    }
    this.renderTrafficButton(show);
    this.setState({ showTraffic: show });
  };

  canCurrentMapTypeShowLiveTraffic = (): boolean => {
    if (!this.map) {
      return false;
    }
    const enabledMapTypes = [MAP_TYPES.ROADMAP, MAP_TYPES.HYBRID, MAP_TYPES.SATELLITE];
    const currentMapType = this.map.getMapTypeId();
    const match = enabledMapTypes.find(item => item === currentMapType);
    return !!match;
  };

  generateNewTrafficLayer = (): google.maps.TrafficLayer => {
    if (this.traffic) {
      this.traffic.setMap(null);
    }
    return new window.google.maps.TrafficLayer({
      autoRefresh: true,
    });
  };

  renderRecenterMapButton = () => {
    if (!this.map) {
      return;
    }

    const controlButtonDiv = document.createElement('div');
    const root = createRoot(controlButtonDiv);
    root.render(<RecenterMapBtn onClick={this.reCenterMap} />);
    this.controlRoots.push(root);
    this.map.controls[this.mapPositions.rightBottom].push(controlButtonDiv);
  };

  handleTrafficButtonClick = (
    _e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    isEnabled: boolean,
  ) => {
    this.showLiveTraffic(isEnabled);
  };

  renderTrafficButton(colorImg: boolean) {
    if (!this.map) {
      return;
    }

    const controlButtonDiv = document.createElement('div');
    const root = createRoot(controlButtonDiv);
    root.render(
      <TrafficBtn
        onClick={this.handleTrafficButtonClick}
        colorImg={colorImg}
        userUrl={this.props.userUrl!}
        currentRegion={this.props.currentRegion?.url || ''}
        selectedProductLine={this.props.selectedProductLine!}
      />,
    );

    this.controlRoots.push(root);
    this.map.controls[this.mapPositions.rightBottom].push(controlButtonDiv);
  }

  zoomToBounds = () => {
    if (!this.map) {
      return;
    }

    let boundsCount = 0;
    let isOnlyTruckMarkers = true;
    const bounds = new window.google.maps.LatLngBounds();
    const extendBounds = (lat?: number, lng?: number): number => {
      const point = new window.google.maps.LatLng(lat ?? 0, lng ?? 0);
      bounds.extend(point);
      return 1;
    };

    const { currentRegion, plantList, ordersList, activeMarkers, selectedMarker } = this.props;

    if (selectedMarker) {
      let { lat, lng } = selectedMarker;
      lat ??= (selectedMarker as MapMarker)?.data?.lat;
      lng ??= (selectedMarker as MapMarker)?.data?.lng;
      if (isValidLatLng(lat ?? 0, lng ?? 0)) {
        extendBounds(lat, lng);
        this.validateAndSetMapCenter(bounds.getCenter());
        this.handleZoomChanged(this.props.currentZoom);
      }
      return;
    }

    if (activeMarkers.length === 1) {
      let boundsLocation: LocationDto | undefined = currentRegion.location;
      boundsLocation = activeMarkers[0].location ?? activeMarkers[0]?.data.positionDetails.location;
      const lat = boundsLocation?.latitude ?? boundsLocation?.lat ?? 0;
      const lng = boundsLocation?.longitude ?? boundsLocation?.lng ?? 0;
      if (isValidLatLng(lat, lng)) {
        const singleMarkerOffset = 0.0003;
        boundsCount += extendBounds(lat + singleMarkerOffset, lng + singleMarkerOffset);
      }
    } else {
      activeMarkers.forEach((marker: MapMarker) => {
        const position = marker.data.positionDetails;
        const { latitude, longitude } = position?.location || { latitude: 0, longitude: 0 };
        boundsCount += extendBounds(latitude, longitude);
      });
    }

    // add plants to map
    if (plantList) {
      plantList.forEach(plant => {
        const { latitude, longitude } = plant.location || { latitude: 0, longitude: 0 };
        boundsCount += extendBounds(latitude, longitude);
      });
      isOnlyTruckMarkers = false;
    }

    // add orders to map
    if (ordersList && ordersList.length > 0) {
      ordersList.forEach(order => {
        const { latitude, longitude } = order.geoZone?.zone.circle?.center || {
          latitude: 0,
          longitude: 0,
        };
        boundsCount += extendBounds(latitude, longitude);
      });
      isOnlyTruckMarkers = false;
    }

    if (boundsCount <= 1 || isOnlyTruckMarkers) {
      this.validateAndSetMapCenter(bounds.getCenter());
    } else {
      this.validateAndFitMapBounds(bounds);
    }
  };

  validateAndSetMapCenter = (center: google.maps.LatLng): void => {
    if (!center || !this.map) {
      return;
    }
    const lat = center.lat();
    const lng = center.lng();
    if (!isValidLatLng(lat, lng)) {
      recordLog(LogLevel.Debug, 'Invalid center provided', { lat, lng });
      return;
    }
    this.map.panTo(center);
    if (this.props.handleCenterChanged) {
      this.props.handleCenterChanged({ lat: center.lat(), lng: center.lng() });
    }
    this.handleZoomChanged(this.props.currentZoom);
  };

  validateAndFitMapBounds = (bounds: google.maps.LatLngBounds): void => {
    if (!bounds || !this.map) {
      return;
    }
    const lat = bounds.getCenter().lat();
    const lng = bounds.getCenter().lng();
    if (!isValidLatLng(lat, lng)) {
      recordLog(LogLevel.Debug, 'Invalid bounds provided', { lat, lng });
      return;
    }
    this.map.panToBounds(bounds);
    this.handleZoomChanged(this.props.currentZoom);
  };

  reCenterMap = () => this.zoomToBounds();

  generateTruckAndDriverMarkers = () => {
    const { activeMarkers, sortedOpenMarkers } = this.props;
    const positionMarkerElements: JSX.Element[] = [];
    const selectedItemId = this.props.selectedMarker?.markerId;

    activeMarkers.forEach((marker: MapMarker) => {
      const { positionDetails } = marker.data;
      if (!positionDetails) {
        recordLog(LogLevel.Debug, 'Can not create the marker without a valid associated position', {
          marker,
        });
        return;
      }

      const sourceId = determineMapMarkerId(marker);
      const isOpen = sortedOpenMarkers.some(
        (marker: OpenMapMarker) => sourceId === determineMapMarkerId(marker),
      );
      const zIndex = this.calculateZIndex(marker?.id, isOpen);
      const infoPaneZIndex = this.calculateZIndex(marker?.id, isOpen, true);
      // Include position data in key to trigger re-renders when WebSocket updates change location/status
      const basePositionKey = `${marker.type}_${marker?.id}_${positionDetails?.timestamp || positionDetails?.id}`;
      const positionKey = `${basePositionKey}_${positionDetails?.location?.lat || 0}_${positionDetails?.location?.lng || 0}`;
      const isPredictiveRouteShowing = this.currentRouteMarkerId === sourceId;
      const markerData: PositionMarkerDataInfo = { ...marker.data, trackedObjectName: marker.name };
      const positionMarker = (
        <PositionMarker
          isRouteHistory={false}
          key={positionKey}
          position={positionDetails}
          zIndex={zIndex}
          infoPaneZIndex={infoPaneZIndex}
          mapRef={this.map}
          isSelected={isOpen}
          selectedItemId={selectedItemId}
          markerData={markerData}
          markerStatusMap={
            this.props.markerStatusMapResolver
              ? this.props.markerStatusMapResolver(positionDetails)
              : this.props.markerStatusMap
          }
          mapType={this.state.selectedMapType as MapTypes}
          onClick={this.onMarkerClick(marker)}
          onCloseClick={this.onMarkerClose(marker)}
          onLogoutDriver={this.handleLogoutDriver(positionDetails)}
          driverInfoMessageCallback={this.props.driverInfoMessageCallback}
          ticketInfoTicketsCallBack={this.props.ticketInfoTicketsCallBack}
          onShowPredictiveRoute={() => this.handleShowPredictiveRoute(marker, positionDetails)}
          isPredictiveRouteShowing={isPredictiveRouteShowing}
          isFollowing={this.state.followedMarkerId === sourceId}
          onToggleFollow={this.handleFollowToggle}
          isMessagingEnabled={this.props.isMessagingEnabled}
        />
      );
      positionMarkerElements.push(positionMarker);
    });
    return positionMarkerElements;
  };

  renderJobsites = (plantColors: any) => {
    const { ordersList, currentRegionId, selectedProductLine, popupToggleMap } = this.props;
    if (!ordersList) {
      return [];
    }

    return ordersList.map(order => {
      const markerColor = plantColors && plantColors[order?.plant?.url ?? ''];
      const orderId = order.id.toString();
      const isOpen =
        isMarkerOpen(
          popupToggleMap,
          currentRegionId,
          selectedProductLine as ProductLine,
          orderId,
        ) ?? false;

      const marker: MarkerType = {
        markerId: orderId,
        type: MapMarkerType.Order,
        markerData: order,
      };

      const orderInfoPanelZIndex = this.calculateZIndex(orderId, isOpen);

      return (
        <JobsiteMarker
          key={order.id}
          order={order}
          plantList={this.props.plantList}
          mapRef={this.map}
          showGeofence={this.props.showJobsitesGeofences}
          markerColor={markerColor}
          onClick={this.onMarkerClick(marker)}
          onCloseClick={this.onMarkerClose(marker)}
          isSelected={isOpen}
          orderDetailsCallback={this.handleOrderDetailsCallback(marker)}
          messageDriversOnOrder={this.props.messageDriversOnOrder}
          zIndex={orderInfoPanelZIndex}
        />
      );
    });
  };

  prevChange = {};

  onChange(change: MapChangeEvent) {
    if (!this.map) return;
    if (isEqual(this.prevChange, change)) {
      return;
    }

    this.prevChange = change;
    if (this.props.onChange !== undefined) {
      this.props.onChange(change);
    }
  }

  onCircleLoad = (circle: google.maps.Circle) => {
    this.circleRef = circle;
    if (!this.state.isMapHasBeenBounded) {
      this.setState({ isMapHasBeenBounded: true }, () => this.zoomToBoundsCP());
    }
  };

  onCircleUnmount = () => {
    this.listenersRef = [];
    this.circleRef = undefined;
  };

  onCircleChanged = () => {
    if (!this.map) return;
    this.onCircleComplete(this.circleRef!);
  };

  onCircleComplete = (circle?: google.maps.Circle) => {
    if (!this.map) return;
    if (!circle) return;
    const center = mapsToLocationDto(circle.getCenter());
    this.onChange({
      circle: {
        center: { lat: center.latitude ?? 0, lng: center.longitude ?? 0 },
        radius: circle.getRadius(),
      },
      polygon: undefined,
    });
  };

  renderGeozoneCircle = (shapeStyles: ShapeStyles) => {
    const zone = this.props.currentGeozone?.zone;
    const circleCenter = zone?.circle?.center;
    if (!circleCenter) {
      return undefined;
    }

    return (
      <Circle
        center={{
          lat: circleCenter.latitude!,
          lng: circleCenter.longitude!,
        }}
        onLoad={this.onCircleLoad}
        radius={zone?.circle?.radius}
        onCenterChanged={this.onCircleChanged}
        onUnmount={this.onCircleUnmount}
        options={shapeStyles}
      />
    );
  };

  onPolygonChanged = () => {
    this.onPolygonComplete(this.polygonRef!);
  };

  onPolygonUnmount = () => {
    this.listenersRef!.forEach(lis => lis.remove());
    this.listenersRef = [];
    this.polygonRef = undefined;
  };

  onPolygonComplete = (polygon: google.maps.Polygon) => {
    const newPolygon = polygon
      .getPath()
      .getArray()
      .map(point => ({
        lat: point.lat(),
        lng: point.lng(),
      }));
    const points = TTMap.googleMapsToPostGISPoints(newPolygon) ?? [];
    this.onChange({
      circle: undefined,
      polygon: {
        points: points.map(p => ({ lat: p.latitude, lng: p.longitude })),
      },
    });
  };

  static googleMapsToPostGISPoints = (points?: { lat: number; lng: number }[]) => {
    if (!points) return null;

    points.push(points[0]);
    return points.map(point => ({
      latitude: point.lat,
      longitude: point.lng,
    }));
  };

  getGeofenceStyle(zoneType?: ZoneType) {
    const shapeStyles: ShapeStyles = {
      fillColor: '#67B4F6',
      strokeColor: '#67B4F6',
      fillOpacity: 0.25,
      strokeWeight: 2,
      clickable: false,
      zIndex: 1,
      draggable: true,
      editable: this.props.isEditable ?? false,
    };

    type GeozoneType = typeof GEOZONE_SHAPE_STYLE;
    type GeozoneShapeStyle = {
      fillColor: string;
      strokeColor: string;
      zIndex: number;
    };

    const zone: { [geozoneName: string]: GeozoneShapeStyle } = {};

    zone[GEOZONE_TYPE.PLANT] = {
      ...GEOZONE_SHAPE_STYLE[GEOZONE_TYPE.PLANT as keyof GeozoneType],
      zIndex: 3,
    };
    zone[GEOZONE_TYPE.INQUEUE] = {
      ...GEOZONE_SHAPE_STYLE[GEOZONE_TYPE.INQUEUE as keyof GeozoneType],
      zIndex: 2,
    };
    zone[GEOZONE_TYPE.RETURN] = {
      ...GEOZONE_SHAPE_STYLE[GEOZONE_TYPE.RETURN as keyof GeozoneType],
      zIndex: 1,
    };

    return zoneType ? { ...shapeStyles, ...zone[zoneType] } : shapeStyles;
  }

  onPolygonLoad = (polygon: google.maps.Polygon) => {
    this.polygonRef = polygon;
    if (!this.listenersRef) this.listenersRef = [];
    const path = polygon.getPath();
    this.listenersRef.forEach(lis => lis.remove());
    this.listenersRef = [
      path.addListener('set_at', this.onPolygonChanged),
      path.addListener('insert_at', this.onPolygonChanged),
      path.addListener('remove_at', this.onPolygonChanged),
    ];

    if (!this.state.isMapHasBeenBounded) {
      this.setState({ isMapHasBeenBounded: true }, () => this.zoomToBoundsCP());
    }
  };

  isValidLocation = () => {
    const location = this.props.currentGeozone?.location;
    const lat = location?.latitude ?? 0;
    const lng = location?.longitude ?? 0;
    return lat !== 0 || lng !== 0;
  };

  zoomToBoundsCP = () => {
    if (!this.map) return;
    const bounds = new window.google.maps.LatLngBounds();

    if (this.isValidLocation()) {
      const location = this.props.currentGeozone?.location;
      bounds.extend(
        new window.google.maps.LatLng(
          (location?.latitude ?? 0) - 0.001,
          (location?.longitude ?? 0) - 0.001,
        ),
      );
      bounds.extend(
        new window.google.maps.LatLng(
          (location?.latitude ?? 0) + 0.001,
          (location?.longitude ?? 0) + 0.001,
        ),
      );
    }

    if (this.circleRef) {
      bounds.union(this.circleRef.getBounds()!);
    }

    if (this.polygonRef) {
      this.polygonRef
        .getPath()
        .getArray()
        .forEach(point => {
          bounds.extend(point);
        });
    }

    this.validateAndFitMapBounds(bounds);
  };

  generateGeozonePolygon = (shapeStyles: ShapeStyles) => {
    const zone = this.props.currentGeozone?.zone;
    const polygonPoints = zone?.polygon?.points;
    if (!polygonPoints) {
      return undefined;
    }

    return (
      <Polygon
        path={postGISToGoogleMapsPoints(polygonPoints) as any}
        onLoad={this.onPolygonLoad}
        editable
        // Event used when dragging the whole Polygon
        onDragEnd={this.onPolygonChanged}
        // Event used when manipulating and adding points
        onMouseUp={this.onPolygonChanged}
        onUnmount={this.onPolygonUnmount}
        options={shapeStyles}
      />
    );
  };

  onMarkerClick = (marker: MarkerType) => (): void => {
    const nextMarkerId = determineMapMarkerId(marker);
    if (nextMarkerId && this.currentRouteMarkerId && this.currentRouteMarkerId !== nextMarkerId) {
      this.clearPredictiveRoute(nextMarkerId, 'marker-selection');
    }
    marker.selectedFrom = MarkerSelectedFrom.MapMarker;
    this.props.onMarkerClick!(marker);
  };

  onMarkerClose = (marker: MarkerType) => (): void => this.props.onMarkerClose!(marker);

  handleLogoutDriver = (position: any) => (): void => this.props.handleLogoutDriver!(position);

  handleZoomChanged = (zoom?: number): void => {
    if (!this.map || zoom == null) {
      return;
    }
    const alreadyAtZoomLevel = zoom === this.state.currentZoom;
    if (alreadyAtZoomLevel) {
      return;
    }
    this.setState({ currentZoom: zoom });
    this.props.onZoomChange(zoom);
  };

  handleOrderDetailsCallback = (marker: MarkerType) => (): void =>
    this.props.orderDetailsCallback!(marker);

  handleShowPredictiveRoute = (marker: MapMarker, position: any): void => {
    this.showPredictiveRoute(marker, position);
  };

  /**
   * Retrieves cached directions from Redux store using the service
   */
  private getCachedDirections(ticketId: string): google.maps.DirectionsResult | null {
    const { directionsCache } = this.props;

    if (!directionsCache) {
      return null;
    }

    return directionsCacheService.getCachedDirections(directionsCache, ticketId);
  }

  /**
   * Renders the directions result on the map using DirectionsRenderer.
   */
  private renderDirectionsResult(result: google.maps.DirectionsResult, markerId: string): void {
    if (!this.directionsRenderer) {
      this.directionsRenderer = new google.maps.DirectionsRenderer({
        suppressMarkers: true,
        polylineOptions: {
          strokeColor: '#4285F4',
          strokeWeight: 5,
          strokeOpacity: 0.8,
        },
      });
    }

    if (!this.map) {
      recordLog(LogLevel.Error, 'Cannot show predictive route: map is null when setting route', {
        markerId,
      });
      return;
    }

    this.directionsRenderer.setMap(this.map);
    this.directionsRenderer.setDirections(result);
    this.currentRouteMarkerId = markerId;
    this.forceUpdate();
  }

  private clearPredictiveRoute(nextMarkerId?: string, reason: string = 'marker-change'): void {
    const previousMarkerId = this.currentRouteMarkerId;
    const hadRenderer = !!this.directionsRenderer;

    if (this.directionsRenderer) {
      this.directionsRenderer.setMap(null);
      this.directionsRenderer = null;
    }

    if (previousMarkerId) {
      recordLog(LogLevel.Debug, 'Cleared predictive route', {
        previousMarkerId,
        nextMarkerId,
        reason,
      });
    }

    this.currentRouteMarkerId = null;

    if (previousMarkerId || hadRenderer) {
      this.forceUpdate();
    }
  }

  /**
   * Resolves the source plant's location for a ticket. Checks the local plant
   * lists first (prop-provided and state slice — either can be a scoped
   * subset, e.g. the Incoming Materials view passes destination plants only,
   * while `state.plantList` is region-scoped) and falls back to a one-off BE
   * fetch for cross-region tickets whose source plant isn't loaded anywhere
   * on the client. Returns null when the plant can't be resolved or has no
   * usable coordinates.
   */
  private async resolveSourcePlantLocation(
    plantUrl: string,
  ): Promise<{ lat: number; lng: number } | null> {
    const localPlant = [this.props.plantList, this.props.plantListFromState]
      .flatMap(list => (Array.isArray(list) ? list : []))
      .find(p => p.url === plantUrl);

    let location = localPlant?.location;
    if (!location) {
      const plantId = getIdFromUrl(plantUrl);
      if (plantId) {
        try {
          const fetched = await fetchPlant(Number(plantId));
          location = fetched?.location;
        } catch (e) {
          recordLog(LogLevel.Error, 'Predictive route: failed to fetch source plant', {
            plantUrl,
            error: e,
          });
        }
      }
    }

    if (!location) return null;
    const lat = location.latitude ?? location.lat ?? 0;
    const lng = location.longitude ?? location.lng ?? 0;
    if (!isValidLatLng(lat, lng)) return null;
    return { lat, lng };
  }

  showPredictiveRoute = async (marker: MapMarker, _position: any): Promise<void> => {
    const markerId = determineMapMarkerId(marker);
    if (!markerId) {
      recordLog(LogLevel.Debug, 'Cannot show predictive route: invalid marker ID', { marker });
      return;
    }

    if (!this.map) {
      recordLog(LogLevel.Error, 'Cannot show predictive route: map is not initialized', {
        markerId,
      });
      return;
    }

    if (this.currentRouteMarkerId === markerId) {
      this.clearPredictiveRoute(undefined, 'toggle-off');
      return;
    }

    if (this.currentRouteMarkerId && this.currentRouteMarkerId !== markerId) {
      this.clearPredictiveRoute(markerId, 'switch-to-new-marker');
    }

    const { ticket } = marker.data;
    if (!ticket || !ticket.geoZone || !ticket.geoZone.zone || !ticket.geoZone.zone.circle) {
      recordLog(LogLevel.Debug, 'Cannot show predictive route: no valid ticket geozone', {
        markerId,
        ticket,
      });
      return;
    }

    if (!ticket.id) {
      recordLog(LogLevel.Error, 'Cannot show predictive route: ticket ID is missing', { ticket });
      return;
    }

    const ticketId = ticket.id.toString();
    recordLog(LogLevel.Debug, 'Preparing predictive route for ticket', { markerId, ticketId });

    if (!ticket.plant?.url) {
      recordLog(LogLevel.Error, 'Cannot show predictive route: ticket has no plant URL', {
        ticketId,
        ticket,
      });
      return;
    }

    // Cache is keyed by ticket id alone, which only makes sense because the
    // route origin (source plant) is static per ticket. Check the cache before
    // doing any plant resolution work.
    const cachedResult = this.getCachedDirections(ticketId);
    if (cachedResult) {
      recordLog(LogLevel.Debug, 'Using cached predictive route result', { markerId, ticketId });
      this.renderDirectionsResult(cachedResult, markerId);
      return;
    }

    const origin = await this.resolveSourcePlantLocation(ticket.plant.url);
    if (!origin) {
      recordLog(LogLevel.Error, 'Cannot show predictive route: source plant has no location', {
        ticketId,
        plantUrl: ticket.plant.url,
      });
      return;
    }

    // The user may have toggled the route off (or switched markers) during the
    // await above; bail if we no longer want this route shown.
    if (this.currentRouteMarkerId && this.currentRouteMarkerId !== markerId) {
      return;
    }

    const destinationCenter = ticket.geoZone.zone.circle.center;
    if (!destinationCenter) {
      recordLog(LogLevel.Error, 'Cannot show predictive route: destination center is undefined', {
        ticketId,
        ticket,
      });
      return;
    }

    const hasLat = destinationCenter.latitude != null || destinationCenter.lat != null;
    const hasLng = destinationCenter.longitude != null || destinationCenter.lng != null;
    if (!hasLat || !hasLng) {
      recordLog(LogLevel.Error, 'Cannot show predictive route: missing destination coordinates', {
        destination: destinationCenter,
        ticketId,
      });
      return;
    }

    const destLat = destinationCenter.latitude ?? destinationCenter.lat ?? 0;
    const destLng = destinationCenter.longitude ?? destinationCenter.lng ?? 0;

    if (destLat === 0 || destLng === 0) {
      recordLog(LogLevel.Error, 'Cannot show predictive route: destination coordinates are zero', {
        destination: destinationCenter,
        ticketId,
      });
      return;
    }

    const originLocation = { latitude: origin.lat, longitude: origin.lng };
    const destinationLocation = { latitude: destLat, longitude: destLng };
    const destination = { lat: destLat, lng: destLng };

    recordLog(LogLevel.Debug, 'Requesting predictive route directions', {
      markerId,
      ticketId,
      origin: originLocation,
      destination: destinationLocation,
    });
    getDirections([originLocation, destinationLocation], (result, status) => {
      recordLog(LogLevel.Debug, 'Predictive route directions callback', {
        markerId,
        ticketId,
        status,
        hasResult: !!result,
      });
      if (status === google.maps.DirectionsStatus.OK && result) {
        const cacheKey = DirectionsCacheService.getCacheKey(ticketId);
        this.props.cacheDirectionsResult(cacheKey, result, ticketId, origin, destination);

        recordLog(LogLevel.Debug, 'Rendering predictive route directions', { markerId, ticketId });
        this.renderDirectionsResult(result, markerId);
      } else {
        recordLog(LogLevel.Error, 'Failed to get directions', {
          status,
          result,
          origin: originLocation,
          destination: destinationLocation,
          ticketId,
        });
        this.showRouteErrorDialog(status);
      }
    });
  };

  private static getRouteErrorMessage(status: google.maps.DirectionsStatus): string {
    switch (status) {
      case google.maps.DirectionsStatus.NOT_FOUND:
        return 'Unable to find a route. The origin, destination, or waypoints could not be geocoded.';
      case google.maps.DirectionsStatus.ZERO_RESULTS:
        return 'No route could be found between the origin and destination.';
      case google.maps.DirectionsStatus.OVER_QUERY_LIMIT:
        return 'The route service has received too many requests. Please try again later.';
      case google.maps.DirectionsStatus.REQUEST_DENIED:
        return 'The route service request was denied. Please contact support if this issue persists.';
      case google.maps.DirectionsStatus.INVALID_REQUEST:
        return 'The route request was invalid. Please verify the origin and destination locations.';
      case google.maps.DirectionsStatus.UNKNOWN_ERROR:
        return 'An unknown error occurred while calculating the route. Please try again.';
      default:
        return 'Unable to calculate a route for this location. Please verify the origin and destination are valid.';
    }
  }

  showRouteErrorDialog = (status: google.maps.DirectionsStatus): void => {
    const errorMessage = TTMap.getRouteErrorMessage(status);
    this.setState({
      routeErrorDialogOpen: true,
      routeErrorMessage: errorMessage,
    });
  };

  closeRouteErrorDialog = (): void => {
    this.setState({
      routeErrorDialogOpen: false,
      routeErrorMessage: '',
    });
  };

  handleupdateToFieldValueWithPlantDrivers = (url: string): void =>
    this.props.updateToFieldValueWithPlantDrivers!(url);

  assignPlantColors = () => {
    const { plantList } = this.props;

    if (!plantList) {
      return {};
    }

    const plantColorAssignments = plantList.reduce((arr, plant) => {
      const { url } = plant;
      const colorIndex = plant.id % markerColors.length;
      const colorAssignment = { ...arr, [url!]: markerColors[colorIndex] };

      return colorAssignment;
    }, {});

    return plantColorAssignments;
  };

  generatePlants = (plantColors: any) => {
    if (!this.props.plantList) {
      return undefined;
    }

    return this.props.plantList.map(plant => {
      const markerColor = plantColors && plantColors[plant.url!];
      const plantId = plant.id.toString();
      const isPlantOpen = isMarkerOpen(
        this.props.popupToggleMap,
        this.props.currentRegionId,
        this.props.selectedProductLine as ProductLine,
        plantId,
      );
      const isOpen = isPlantOpen ?? false;

      const marker: MarkerType = {
        markerId: plantId,
        type: MapMarkerType.Plant,
        markerData: plant,
      };

      const zIndex = this.calculateZIndex(plantId, isOpen);

      return (
        <PlantMarkerWithInfoPane
          key={plant.id}
          plant={plant}
          mapRef={this.map}
          showGeofence={this.props.showPlantsGeofences}
          markerColor={markerColor}
          updateToFieldValueWithPlantDrivers={this.handleupdateToFieldValueWithPlantDrivers}
          isMessagingEnabled={this.props.isMessagingEnabled}
          onClick={this.onMarkerClick(marker)}
          onCloseClick={this.onMarkerClose(marker)}
          isSelected={isOpen}
          zIndex={zIndex}
        />
      );
    });
  };

  static renderTableRow = (header: string, value: string | undefined) => {
    const val = value === '' ? undefined : value;
    return (
      <tr>
        <th>{header}</th>
        <td>{val ?? 'N/A'}</td>
      </tr>
    );
  };

  handleMapTypeStyleUpdates = (): void => {
    if (!this.map) {
      return;
    }

    // If a mapId is present (configured via REACT_APP_GOOGLE_MAP_ID), styles
    // must be managed through the Maps Cloud Console. Avoid calling
    // setOptions({ styles }) when a mapId is present to prevent runtime errors.
    // See: https://developers.google.com/maps/documentation/javascript/styling#cloud_tooling
    if (process.env.REACT_APP_GOOGLE_MAP_ID) {
      return;
    }

    mapStyles.forEach(style => {
      if (!style || style.elementType !== 'labels.text.fill' || !Array.isArray(style.stylers)) {
        return;
      }

      const currentMapType = determineMapType(this.map);
      if (!currentMapType) {
        return;
      }
      const lightTextFillColorMapTypes: MapTypes[] = [MAP_TYPES.SATELLITE, MAP_TYPES.HYBRID];
      const useLightText = lightTextFillColorMapTypes.includes(currentMapType);
      const labelTextFillColor = useLightText ? '#e0e0e0' : '#4c5862';

      const colorStyle = style.stylers?.find((styler: any) => typeof styler.color === 'string');
      if (colorStyle && 'color' in colorStyle) {
        colorStyle.color = labelTextFillColor;
      }
    });
    this.map.setOptions({ styles: mapStyles });
  };

  mapMarkers = (): (JSX.Element | JSX.Element[] | undefined)[] => {
    const geoFenceStyle = this.getGeofenceStyle(this.props.geofenceAreaType);
    const plantColors = this.assignPlantColors();
    const items = [
      this.generateTruckAndDriverMarkers(),
      this.generatePlants(plantColors),
      this.renderJobsites(plantColors),
      this.renderGeozoneCircle(geoFenceStyle),
      this.generateGeozonePolygon(geoFenceStyle),
    ];
    return items;
  };

  render() {
    const markers = this.mapMarkers();
    return (
      <>
        <div ref={this.mapRef} style={containerStyle} id="map" key="map-key" />
        {markers}
        <Dialog
          open={this.state.routeErrorDialogOpen}
          onClose={this.closeRouteErrorDialog}
          aria-labelledby="route-error-dialog-title"
          aria-describedby="route-error-dialog-description"
        >
          <DialogTitle id="route-error-dialog-title">Unable to Calculate Route</DialogTitle>
          <DialogContent>
            <DialogContentText id="route-error-dialog-description">
              {this.state.routeErrorMessage}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeRouteErrorDialog} color="primary" autoFocus>
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
}

interface RegionWithLocation extends UrlKeyDto {
  location?: LocationDto;
}

export interface MarkerDataType {
  markerId: string;
  type: string;
  driverDetails: any;
  truckDetails?: PositionMarkerDataInfo;
  ticket: UrlKeyDto;
  status?: string;
}

export interface MarkerType {
  markerId: string;
  type: string;
  markerData?: OrderDto | MarkerDataType | PlantDto | undefined;
  driverDetails?: any;
  truckDetails?: any;
  positionDetails?: PositionDetailsDto;
  ticket?: TicketDto | null | undefined;
  selectedFrom?: MarkerSelectedFromSource;
}

export function mapStateToProps(state: ReduxState) {
  return {
    routeHistoryHoverSelection: state.routeHistoryHoverSelection,
    selectedMarker: state.selectedMarker,
    userUrl: state.userUrl,
    activeMarkers: state.activeMarkers,
    currentRegionId: determineRegionId(state.currentRegion as UserRegionDto),
    sortedOpenMarkers: sortedOpenMarkersMatchingMapFilter(state),
    directionsCache: state.directionsCache,
    plantListFromState: state.plantList,
  };
}

const mapDispatchToProps = {
  cacheDirectionsResult,
};

type ReduxProps = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

interface OwnProps {
  geofenceAreaType?: ZoneType;
  isEditable?: boolean;
  onChange?: (evt: MapChangeEvent) => void;
  activeMarkers: MapMarker[];
}

export interface TTMapState {
  showTraffic: boolean;
  isMapHasBeenBounded: boolean;
  userPreferences?: UserPreferencesDto;
  selectedMapType: string;
  pauseMapRendering: boolean;
  currentZoom: number | undefined;
  shouldUpdateCenter: boolean | null;
  shouldUpdateZoom: boolean | null;
  followedMarkerId: string | null;
  routeErrorDialogOpen: boolean;
  routeErrorMessage: string;
}

export type TTMapProps = OwnProps &
  ReduxProps & {
    center?: DefaultLocation;
    currentRegion: RegionWithLocation;
    plantList?: PlantDto[];
    popupToggleMap: PopupToggleMap;
    ordersList?: OrderDto[];
    showPlantsGeofences?: boolean;
    showJobsitesGeofences?: boolean;
    currentGeozone?: GeoZoneDto;
    selectedProductLine?: string;
    onMarkerClick?: (marker: MarkerType) => void;
    onMarkerClose?: (marker: MarkerType) => void;
    driverInfoMessageCallback?: (obj: any) => void;
    updateToFieldValueWithPlantDrivers: (url: string) => void;
    ticketInfoTicketsCallBack?: (obj: any) => void;
    messageDriversOnOrder?: (order: OrderDto) => void;
    handleCenterChanged?: (center: { lat: number; lng: number }) => void;
    handleLogoutDriver?: (obj: any) => void;
    orderDetailsCallback?: (marker: MarkerType) => void;
    onZoomChange: (zoomLevel: number) => void;
    onMapReady?: () => void;
    currentZoom?: number;
    markerStatusMap?: {
      [x: string]: string | undefined;
    };
    /**
     * DEV-3924: per-marker override of markerStatusMap. When provided, each truck marker's
     * status names are derived from its own position (e.g. the paired ticket's origination
     * plant product line on the Incoming Materials map) instead of the shared map.
     */
    markerStatusMapResolver?: (positionDetails: PositionDetailsDto) => {
      [x: string]: string | undefined;
    };
    isMessagingEnabled: boolean;
  };

export default connect(mapStateToProps, mapDispatchToProps)(TTMap);
